import { ISchemaRef, IType, TypeCategory } from '@augloop/schemas/dist/src/client/schemas';

/* eslint-disable @typescript-eslint/naming-convention */
export const UndefinedType: IType = { category: TypeCategory.Undefined };
export const BooleanType: IType = { category: TypeCategory.Boolean };
export const NumberType: IType = { category: TypeCategory.Number };
export const StringType: IType = { category: TypeCategory.String };
export const BufferType: IType = { category: TypeCategory.Buffer };
export const FunctionType: IType = { category: TypeCategory.Function };

export const TypeSchema: IType = {
    category: TypeCategory.Schema,
    schema: {
        name: 'Type',
        path: 'type-schema.proto'
    }
};

export const MessageSchema: IType = {
    category: TypeCategory.Schema,
    schema: {
        name: 'BufferMessage',
        path: 'message-schema.proto'
    }
};
/* eslint-enable @typescript-eslint/naming-convention */

export const isTypePrimitive = (objectType: IType): boolean => {
    if (objectType == null) {
        return false;
    }

    switch (objectType.category) {
        case TypeCategory.Boolean:
        case TypeCategory.Number:
        case TypeCategory.String:
            return true;
        default:
            return false;
    }
};

// This serves to flatten the underlying concatenated string
const flattenStr = (str: string) => {
    Number(str);
    return str;
};

export const areTypesEqual = (type1: IType, type2: IType): boolean => {
    return typeToString(type1) === typeToString(type2);
};

// Stringify IType with stable ordering of properties
export const typeToString = (objectType: IType): string => {
    if (objectType === undefined) {
        return '';
    }

    if (objectType === null) {
        return 'null';
    }

    const pairs = Object.keys(objectType)
        .sort()
        .map(key => propertyToString(key, objectType[key]))
        .filter(pair => !!pair); // Remove non-truthy strings

    return flattenStr('{'.concat(pairs.join(','))
        .concat('}'));
};

// Stringify ISchemaRef with stable ordering of properties
const getSchemaRefToString = (schema: ISchemaRef): string => {
    if (schema === undefined) {
        return '';
    }

    if (schema === null) {
        return 'null';
    }

    const pairs = Object.keys(schema)
        .sort()
        .map(key => propertyToString(key, schema[key]))
        .filter(pair => !!pair);  // Remove non-truthy strings
    return '{'.concat(pairs.join(',')).concat('}');
};

const propertyToString = (propertyName: string, propertyValue: any): string => {
    if (propertyName === 'schema') {
        return `"${propertyName}":${getSchemaRefToString(propertyValue)}`;
    }

    if (propertyName === 'path') {
        return ''; // No schema path, schema name should be unique (e.g., via namespaces)
    }

    if (propertyName === 'nestedArrayLevel') {
        if (!propertyValue) {
            return ''; // Leave off nestedArrayLevel if it is not truthy
        }

        return `"${propertyName}":${propertyValue}`;
    }

    if (propertyValue === undefined) {
        return '';
    }

    if (typeof propertyValue === 'string') {
        return `"${propertyName}":"${propertyValue}"`; // Quotes
    }

    return `"${propertyName}":${propertyValue}`;
};

export const schemaRefToString = getSchemaRefToString;

// Returns whether type is an array type
export const isArrayType = (objectType: IType): boolean => {
    if (!objectType) {
        return false;
    }

    return objectType.nestedArrayLevel > 0;
};

// Returns an array type corresponding to the given element type
export const arrayTypeFrom = (objectType: IType): IType => {
    // TODO: Use {...objectType} once detective-typescript version can handle it
    // eslint:disable prefer-object-spread
    const clonedType: IType = { ...objectType};
    clonedType.nestedArrayLevel = isArrayType(objectType) ? objectType.nestedArrayLevel + 1 : 1;
    return clonedType;
};

// Returns an element type corresponding to the given array type
export const elementTypeFrom = (objectType: IType): IType => {
    // TODO: Use {...objectType} once detective-typescript version can handle it
    // eslint:disable prefer-object-spread
    const clonedType: IType = { ...objectType};
    clonedType.nestedArrayLevel = isArrayType(objectType) ? objectType.nestedArrayLevel - 1 : 0;
    return clonedType;
};

export interface IField {
    name: string;
    fieldType: IType;
}

export interface ISchemaFileStore {
    /**
     * Loads the contents of a schema file and returns it as a string.
     * @param path Schema file location relative to the root of the store
     * @returns Promise that resolves to the schema file contents
     */
    loadSchemaFile(path: string): Promise<string>;
}

export interface ITypeRegistry {
    /**
     * Registers the given type asynchronously.
     * @param objectType Schema type
     * @returns promise that resolves when done
     */
    registerType(objectType: IType, version?: string): Promise<void>;

    /**
     * Gets the field matching the field name for the given type.
     * @param objectType Schema type
     * @param fieldName Name of the field
     * @returns field
     */
    getField(objectType: IType, fieldName: string, version?: string): IField;

    /**
     * Gets the fields for the given type.
     * @param objectType Schema type
     * @returns fields
     */
    getFields(objectType: IType, version?: string): IField[];

    /**
     * Verifies that an arbitrary object conforms to the type.
     * @param objectType Schema type
     * @param obj Arbitrary object to verify
     * @returns `null` if valid, otherwise the reason why it is not
     */
    verify(objectType: IType, obj: any, version?: string): (string | null);

    /**
     * Serializes an object of a given type.
     * Does not implicitly verify objects.
     * @param objectType Type of the object to serialize
     * @param obj Object to serialize
     * @returns Serialized message
     */
    serialize(objectType: IType, obj: any, version?: string): Buffer;

    /**
     * Deserializes a serialized object.
     * @param objectType Type to deserialize the message to
     * @param obj serialized object
     * @returns Schematized object
     * @throws {Error} If format is invalid
     */
    deserialize(objectType: IType, obj: Buffer, version?: string): any;

    /**
     * Stringifies an object of a given type.
     * Does not implicitly verify objects.
     * @param objectType Type of the object to stringify
     * @param obj Object to stringify
     * @returns Stringified message
     */
    stringify(objectType: IType, obj: any, version?: string): string;

    /**
     * Parses a stringified object.
     * @param objectType Type to parse the message to
     * @param obj Stringified object
     * @returns Parsed object
     * @throws {Error} If format is invalid
     */
    parse(objectType: IType, obj: string, version?: string): any;
}
